import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { toast } from "sonner"
import {
  getGpcSignal,
  getPrivacyChoices,
  resetPrivacyChoices,
  setSaleSharingOptOut,
} from "@/lib/privacyChoices"

/**
 * "Cookie Settings" / privacy choices manager.
 *
 * The app loads no analytics, advertising, or cross-site tracking technology, so this deliberately
 * does not offer fake on/off categories. It exposes the choice that actually has an effect: opting
 * out of any state-law "sale" or "sharing" of personal information. The choice is persisted with a
 * version, reflects Global Privacy Control, and can be reset.
 */
export default function PrivacyChoicesDialog() {
  const [open, setOpen] = useState(false)
  const [optOut, setOptOut] = useState(false)
  const [gpc, setGpc] = useState(false)
  const [source, setSource] = useState<string>("default")

  useEffect(() => {
    if (!open) return
    const choices = getPrivacyChoices()
    setOptOut(choices.saleSharingOptOut)
    setSource(choices.source)
    setGpc(getGpcSignal())
  }, [open])

  const handleSave = () => {
    const saved = setSaleSharingOptOut(optOut)
    setSource(saved.source)
    toast.success(
      saved.saleSharingOptOut
        ? "Saved. Your opt-out of sale or sharing is recorded on this browser."
        : "Saved. Your privacy choices have been updated.",
    )
    setOpen(false)
  }

  const handleReset = () => {
    const reset = resetPrivacyChoices()
    setOptOut(reset.saleSharingOptOut)
    setSource(reset.source)
    toast.success("Your saved choice was cleared for this browser.")
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="text-sm text-gray-300 underline-offset-4 hover:text-white hover:underline"
        >
          Cookie Settings
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg bg-background">
        <DialogHeader>
          <DialogTitle>Cookie settings and privacy choices</DialogTitle>
          <DialogDescription>
            This site uses only strictly necessary browser storage — keeping you signed in, carrying
            your answers between form steps, and protecting forms from automated abuse. There are no
            analytics, advertising, or cross-site tracking technologies to turn on or off, so there is
            nothing here to accept or reject.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md border p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="sale-sharing-opt-out" className="text-sm font-medium">
                  Opt out of sale or sharing of my personal information
                </Label>
                <p className="text-sm text-muted-foreground">
                  We do not sell personal information for money. Some US state privacy laws define
                  "sale" and "sharing" broadly, so we let you record an opt-out that we apply to how
                  your submitted information is passed to our marketing and analysis providers.
                </p>
              </div>
              <Switch
                id="sale-sharing-opt-out"
                checked={optOut}
                onCheckedChange={setOptOut}
                aria-describedby="sale-sharing-help"
              />
            </div>
            <p id="sale-sharing-help" className="mt-3 text-xs text-muted-foreground">
              {gpc
                ? "Your browser is sending a Global Privacy Control signal, so the opt-out is applied automatically."
                : "Your browser is not sending a Global Privacy Control signal. If it does, we apply the opt-out automatically."}{" "}
              Current setting source: {source}.
            </p>
          </div>

          <p className="text-sm text-muted-foreground">
            Choices are stored on this browser only. For full details, see our{" "}
            <Link to="/cookies" className="underline" onClick={() => setOpen(false)}>
              Cookie Policy
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline" onClick={() => setOpen(false)}>
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={handleReset}>
            Reset choices
          </Button>
          <Button onClick={handleSave}>Save choices</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
